import { jsx as _jsx } from "react/jsx-runtime";
import { isIconAccessoryProp } from '#pplx/frontend/aether/components/Accessory/isIconAccessoryProp.js';
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import classNames from 'classnames';
const COLOR_CLASSES = {
    quiet: 'text-secondary',
    quieter: 'text-tertiary',
};
/**
 * A utility component that renders icon sprites, Tabler icon components, or arbitrary React nodes
 * as a flex-aligned accessory. Accessory normalizes the display of different icon types alongside
 * custom content, handling sizing and layout automatically.
 *
 * @category components
 * @keywords accessory, components, utility, component, that, renders, icon, sprites, tabler,
 *   arbitrary, react, nodes, flex-aligned, normalizes, display, different, types, alongside,
 *   custom, content, handling, sizing, layout, automatically, size, content-item, menu,
 *   select-button, chip
 * @whenToUse Use Accessory when building or extending Aether components that need a flexible slot
 *   for icons or custom visual content. It acts as a normalizer: pass a Tabler icon function, an
 *   icon sprite string, or any ReactNode, and Accessory handles the rendering differences. In most
 *   cases you will not use Accessory directly — higher-level components like ContentItem,
 *   DropdownMenu items, and SelectButton accept an accessory or leadingAccessory prop that renders
 *   Accessory internally. Reach for it directly when composing custom list items, nav elements, or
 *   other patterns that need the same icon/content flexibility.
 * @guidelineDo
 *   - Use Accessory to normalize icon and custom content rendering inside composed components like
 *     ContentItem, DropdownMenu items, and SelectButton
 *   - Pass icon sprite definitions via $icon() or Tabler icon components directly as the accessory
 *     prop for automatic sizing and alignment
 *   - Use the size prop to match the parent component context — default for compact layouts, large
 *     for prominent placements
 *   - Wrap custom ReactNode accessories in a flex container with appropriate sizing to maintain
 *     visual consistency with icon accessories
 *   - Apply spacing or positioning in the parent layout/wrapper; Accessory itself preserves a fixed
 *     flex/shrink wrapper for consistent icon alignment
 * @guidelineDont
 *   - Don't use Accessory as a standalone decorative element — it is a composition primitive
 *     designed to be embedded inside other components
 *   - Don't pass interactive elements (buttons, links) as the accessory prop — Accessory is a
 *     display wrapper, not an interactive container
 *   - Don't override the shrink-0 behavior — Accessory intentionally prevents icon compression in
 *     flex layouts
 *   - Don't mix size prop intent with custom-sized ReactNode content — if passing a custom node,
 *     the size prop only affects icon-type accessories
 * @accessibility
 *   - When rendering icon-type accessories, the underlying Icon component handles aria-hidden and
 *     decorative icon semantics automatically
 *   - Custom ReactNode accessories should include their own accessible labeling if they convey
 *     meaningful information — Accessory does not add any ARIA attributes to custom content
 *   - The empty:hidden utility class ensures the wrapper is removed from the accessibility tree
 *     when no content is provided
 *   - Accessory is typically used alongside text labels in parent components (ContentItem,
 *     DropdownMenu items), so icons are decorative and don't need independent labeling
 * @relatedComponents
 *   - icon
 *   - content-item
 *   - menu
 *   - select-button
 *   - chip
 */
export function Accessory({ accessory, size = 'default', color }) {
    return (_jsx("div", { className: classNames('empty:hidden flex shrink-0 items-center', color && COLOR_CLASSES[color]), children: isIconAccessoryProp(accessory) ? (_jsx(Icon, { icon: accessory, size: size === 'large' ? 'medium' : 'small' })) : (accessory) }));
}
//# sourceMappingURL=index.js.map